

    import Vue from "vue";
    import Component from "vue-class-component";
    import { VForm } from "@/vForm";
    import { Ref, Watch } from "vue-property-decorator";
    import * as toastr from "toastr";
    import ApiButton from "@/vue/components/ApiButton.vue";
    import PrimaryButton from "@/vue/components/PrimaryButton.vue";
    import Utils from "@/utilities/Utils";
    import ApiClient from "@/utilities/ApiClient";

    interface ISendResponse {
        emailFailed: boolean;
        isExpired: boolean;
        isWrongCode: boolean;
        isSuccess: boolean;
    }

    @Component({ components: { ApiButton, PrimaryButton } })
    export default class Contact extends Vue {

        contact = {
            name: "",
            email: "",
            phone: "",
            message: "",
            captchaID: "",
            captchaText: "",
            visitID: ""
        };

        didJustSend = false;
        responseErrorMessage = "";
        captchaShouldValidateOnChange = false;
        captchaErrorMessages: Array<string> = [];
        @Ref("contactForm") private readonly contactForm!: VForm;

        async mounted(): Promise<void> {
            await this.refreshCaptcha();
        }

        get captchaSrc() {
            // default image if error?
            return ApiClient.resolveUrl(`api/captcha/image?id=${this.contact.captchaID}`);
        }

        async refreshCaptcha(): Promise<void> {
            const resp: { id: string } = await ApiClient.get("/api/captcha/create");
            this.contact.captchaID = resp.id ?? "";
            this.captchaErrorMessages.length = 0;
        }

        validateEmail(email: string): string|boolean {
            if(Utils.isEmptyOrWhitespace(email)) return "Please enter your email address";
            const isValid = String(email)
                .toLowerCase()
                .match(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);
            const result = !isValid ? "The email address is not valid" : true;
            return result;
        }

        onCaptchaBlur() {
            // start validating after field has been 'visited'
            this.captchaShouldValidateOnChange = true;
            this.validateCaptcha();
        }

        @Watch("captchaText")
        onCaptchaChange() {
            if(this.captchaShouldValidateOnChange) {
                this.validateCaptcha()
            }
        }

        async validateCaptcha(): Promise<void> {
            this.captchaErrorMessages = [];
            if (Utils.isEmptyOrWhitespace(this.contact.captchaText)) {
                this.captchaErrorMessages.length = 0;
                this.captchaErrorMessages.push("Please enter the text");
                return;
            }
            const response: { message: string } = await ApiClient.get(`/api/captcha/verify?id=${this.contact.captchaID}&text=${encodeURIComponent(this.contact.captchaText)}`);
            if (response.message !== "ok") {
                this.captchaErrorMessages.push("The text is incorrect");
            }
        }

        async send(): Promise<void> {
            this.responseErrorMessage = "";
            await this.validateCaptcha();
            const isValid = this.contactForm.validate() && this.captchaErrorMessages.length === 0;

            if (!isValid) {
                toastr.warning("Please fix highlighted issues", "Cannot Send");
                if(this.captchaErrorMessages.length > 0) {
                    this.refreshCaptcha();
                }
                return;
            }

            const response: ISendResponse = await ApiClient.post("/api/contact/send", this.contact, "contact-send");
            if(response.isSuccess) {
                this.responseErrorMessage = "";
                this.didJustSend = true;
            }
            else {
                this.responseErrorMessage =
                    response.isExpired ? "Sorry, the code has expired" :
                    response.isWrongCode ? "Sorry, the code was incorrect" :
                    response.emailFailed ? "Sorry, the email could not be sent" :
                    "Unspecified error";
            }
        }

        sendAnother() {
            this.refreshCaptcha();
            this.contact.captchaText = "";
            this.contactForm.resetValidation();
            this.captchaErrorMessages.length = 0;
            this.didJustSend = false;
        }
    }
